import React from 'react';
import './export-dataset-modal.scss';
import {
  ForgeDialog,
  ForgeScaffold,
  ForgeToolbar,
  ForgeButton,
  ForgeIconButton,
  ForgeButtonToggleGroup,
  ForgeIcon,
  ForgeButtonToggle,
} from '@tylertech/forge-react';
import { isEmpty, isObject, isString } from 'lodash';
import { FeatureFlags } from 'common/feature_flags';
import { View } from 'common/types/view';
import { TransitionSuccessType } from 'common/explore_grid/lib/feature-flag-helpers';
import { QueryMetaSuccess } from 'common/explore_grid/redux/store';
import { ExportFormat, getExtensionForExportFormat, getMimeType } from 'common/downloadLinks';
import { none, Option, some } from 'ts-option';
import UrlEndpoint from './UrlEndpoint';
import { ClientContextVariable } from 'common/types/clientContextVariable';
import { checkStatus } from 'common/notifications/api/helper';
import contentDisposition from 'content-disposition';
import { hideToastById, showToastNow, ToastType } from '../ToastNotification/Toastmaster';
import ProviderDownloadWarning from './ProviderDownloadWarning';
import eventBus from 'common/visualizations/views/agGridReact/helpers/EventBus';
import { ResourceState, ResourceType } from './ExportModalTypes';
import FilteringRadioButtons from './FilteringRadioButtons';
import VersionRadioButtons, { PossibleVersions } from './VersionRadioButtons';
import DownloadFileTab from './DownloadFileTab';
import { getResourceState } from './ResourceStateHelper';
import { defaultHeaders } from 'common/http';
import CopyToClipboard from 'common/components/CopyToClipboard';
import { windowURL } from 'common/window_location';
import getScopedTranslateMethod from 'common/js_utils/getScopedTranslateMethod';

const translate = getScopedTranslateMethod('shared.explore_grid.export_dataset_modal');

const toastHideId = 'export-modal-toast';

export enum TOGGLE_OPTIONS {
  DOWNLOAD_FILE = 'download_file',
  API_ENDPOINT = 'api_endpoint',
  ODATA_ENDPOINT = 'odata_endpoint',
}

export interface StateProps {
  query: Option<TransitionSuccessType>;
  meta?: Option<QueryMetaSuccess>;
  view: View;
  clientContextVariables: ClientContextVariable[];
}

export interface QueryStringObject {
  selectClause: string;
  odataSelectClause?: string;
  whereClause: string;
}

/**
 * Props for the ExportModal component.
 *
 * ExportModal is responsible for the export UI, formats, and download functionality.
 * It works in conjunction with RowCountProvider for displaying row counts.
 *
 * Note: This component should be wrapped with RowCountProvider when row count
 * display is needed in FilteringRadioButtons.
 */
type Props = StateProps & {
  /** Default export option to show when modal opens (download file, API endpoint, or OData) */
  defaultToggleOption?: TOGGLE_OPTIONS;

  /** Revision sequence number for the dataset (used for versioned exports) */
  revisionSeq?: number;

  /** Four-by-four identifier for the dataset */
  fourfour?: string;

  /** Text to display in the modal body describing what's being exported */
  bodyText?: string;

  /** Callback function called when modal closes */
  onDismiss: () => void;

  /**
   * Query string clause for filtering exports. Can be a simple string or structured object.
   * - String: Raw SQL WHERE/SELECT clause (e.g., "WHERE category='food'")
   * - Object: Structured query with separate select and where clauses
   */
  queryStringClause?: string | QueryStringObject;

  /** Whether to show radio buttons for choosing between filtered/full data export */
  showDataToggles?: boolean;

  /** Unique identifier for visualization (used for Excel export events) */
  vizUid?: string;

  /** Whether this export is from a table visualization (affects available formats) */
  isTableViz: boolean | false;

  /** Whether the export modal is currently open (controls dialog visibility) */
  isExportModalOpen?: boolean;
};

export interface State {
  toggleOption: TOGGLE_OPTIONS;
  resourceState: ResourceState;
  selectedFiltered: boolean;
  showFilteredDataOption: boolean;
  selectedVersion: PossibleVersions;
  useResourceName: boolean;
  isExportModalOpen: boolean;
}

export default class ExportModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const selectedVersion = FeatureFlags.value('soda3_exports')
      ? PossibleVersions.Soda3
      : PossibleVersions.Soda2;
    this.state = {
      toggleOption: this.props.defaultToggleOption ?? TOGGLE_OPTIONS.DOWNLOAD_FILE,
      resourceState: getResourceState(
        props.view,
        props.query,
        props.clientContextVariables,
        none,
        props.isTableViz,
        selectedVersion,
        props.fourfour,
        props.revisionSeq,
      ),
      selectedFiltered: this.shouldShowFilteredDownload(),
      selectedVersion,
      showFilteredDataOption: this.shouldShowFilteredDownload(),
      useResourceName: false, // Setting isExportModalOpen to the old default value = TRUE
      isExportModalOpen: this.props.isExportModalOpen ?? true,
    };
  }

  componentDidMount() {
    if (this.props.queryStringClause && !this.queryStringIsEmpty()) {
      this.updateResourceStateQueryString();
    }
  }

  handleToggleChange = (value: string) => this.setState({ toggleOption: value as TOGGLE_OPTIONS });

  handleRadioFilterChange = (selectedFiltered: boolean) =>
    this.setState({ selectedFiltered }, () => this.updateResourceStateQueryString());

  handleRadioVersionChange = (selectedVersion: PossibleVersions) =>
    this.setState({ selectedVersion }, () => this.updateResourceStateQueryString());

  handleUseResourceNameChange = (useName: boolean) =>
    this.setState({ useResourceName: useName }, () => this.updateResourceStateQueryString());

  updateResourceStateQueryString(): void {
    const { queryStringClause } = this.props;
    const odataQueryString = isObject(queryStringClause) ? queryStringClause.odataSelectClause : undefined;
    let queryString;
    // We should only be able to select filtered when there is a queryString
    if (this.state.selectedFiltered && queryStringClause) {
      // Here we are exporting filtered
      queryString = isString(queryStringClause)
        ? some(queryStringClause)
        : some(`${queryStringClause.selectClause} ${queryStringClause.whereClause}`);
    } else {
      // If the export is on a viz, then respect the column selection
      queryString = isObject(queryStringClause) ? some(queryStringClause.selectClause) : none;
    }
    this.setState({
      resourceState: getResourceState(
        this.props.view,
        this.props.query,
        this.props.clientContextVariables,
        queryString,
        this.props.isTableViz,
        this.state.selectedVersion,
        this.props.fourfour,
        this.props.revisionSeq,
        this.state.resourceState,
        this.state.useResourceName,
        odataQueryString,
      ),
    });
  }

  onDownload = () => {
    hideToastById(toastHideId);
    const { selectedDownloadResource } = this.state.resourceState;
    if (!selectedDownloadResource) return;

    showToastNow({
      type: ToastType.DEFAULT,
      content: translate('data_exported'),
      timeout: Infinity,
      hideId: toastHideId,
    });

    if (selectedDownloadResource.value === 'xlsx') {
      eventBus.dispatch(`exportGridData-${this.props.vizUid}`, {
        selectedFiltered: this.state.selectedFiltered,
      });
      this.hideModal();
    } else {
      const isShapefile = selectedDownloadResource.value === 'shapefile';
      const downloadMethod =
        FeatureFlags.value('soda3_exports') && !isShapefile
          ? this.downloadFileRequestSoda3
          : this.downloadFileRequest;
      downloadMethod(selectedDownloadResource);
    }
  };

  onCopyToast = (content: string) =>
    showToastNow({
      type: ToastType.DEFAULT,
      content,
      hideId: toastHideId,
      forgeOptions: { dismissible: true },
    });

  onCopyApiToast = () => this.onCopyToast(translate('api_copied'));
  onCopyOdataToast = () => this.onCopyToast(translate('odata_copied'));

  filteringRadioButtons = (odata = false): JSX.Element | null =>
    this.props.showDataToggles ? (
      <FilteringRadioButtons
        selectedFiltered={this.state.selectedFiltered}
        handleRadioFilterChange={this.handleRadioFilterChange}
        showFilteredDataOption={this.state.showFilteredDataOption}
        odata={odata}
      />
    ) : null;

  versionRadioButtons = (odata = false): JSX.Element | null =>
    FeatureFlags.value('soda3_exports') ? (
      <VersionRadioButtons
        selectedVersion={this.state.selectedVersion}
        handleRadioVersionChange={this.handleRadioVersionChange}
      />
    ) : null;

  downloadWarning = () => {
    const { resourceState, selectedFiltered, toggleOption } = this.state;
    const { vizUid, meta } = this.props;

    return (
      <ProviderDownloadWarning
        selectedFiltered={selectedFiltered}
        isFileDownloadSelected={toggleOption === TOGGLE_OPTIONS.DOWNLOAD_FILE}
        isVisualization={vizUid !== undefined}
        selectedResourceValue={resourceState?.selectedDownloadResource?.value}
        meta={meta}
      />
    );
  };

  apiEndpoint = (): JSX.Element => (
    <UrlEndpoint
      onCopy={this.onCopyApiToast}
      onSelectedUrlChange={this.handleSelectedUrlChange}
      resources={this.state.resourceState.apiResources}
      selectedResource={this.state.resourceState.selectedApiResource}
      filteringRadioButtons={this.filteringRadioButtons()}
      versionRadioButtons={this.versionRadioButtons()}
      useResourceName={this.state.useResourceName}
      onResourceNameUpdate={this.props.view?.resourceName ? this.handleUseResourceNameChange : undefined}
      showApiLimitWarning={false}
      isOdata={false}
      viewUid={this.props.view.id}
    />
  );

  _OdataEndpoint = (): JSX.Element => (
    <UrlEndpoint
      onCopy={this.onCopyOdataToast}
      onSelectedUrlChange={this.handleOdataVersionChange}
      resources={this.state.resourceState.odataResources}
      selectedResource={this.state.resourceState.selectedOdataResource}
      filteringRadioButtons={this.filteringRadioButtons(true)}
      useResourceName={this.state.useResourceName}
      onResourceNameUpdate={this.props.view?.resourceName ? this.handleUseResourceNameChange : undefined}
      showApiLimitWarning={false}
      isOdata={true}
      viewUid={this.props.view.id}
    />
  );

  downloadFile = (): JSX.Element => (
    <DownloadFileTab
      downloadResources={this.state.resourceState.downloadResources}
      selectedDownloadResource={this.state.resourceState.selectedDownloadResource}
      handleExportChange={this.handleExportChange}
      filteringRadioButtons={this.filteringRadioButtons()}
      downloadWarning={this.downloadWarning()}
    />
  );

  handleExportChange = (format: string) =>
    this.setState((prevState) => ({
      resourceState: {
        ...prevState.resourceState,
        selectedDownloadResource: prevState.resourceState.downloadResources.find((v) => v.value === format),
      },
    }));

  handleSelectedUrlChange = (resource: ResourceType) =>
    this.updateResourceStateField('selectedApiResource', resource);

  handleOdataVersionChange = (resource: ResourceType) =>
    this.updateResourceStateField('selectedOdataResource', resource);

  updateResourceStateField = (field: keyof ResourceState, value: any) =>
    this.setState((prevState) => ({
      resourceState: { ...prevState.resourceState, [field]: value },
    }));

  // SODA2 API download handler
  downloadFileRequest = (downloadResource: ResourceType): void => {
    const save: HTMLAnchorElement = document.createElement('a');

    fetch(downloadResource.url, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then((r) => checkStatus(r, `Error exporting data in format ${downloadResource.value}`))
      .then((response) => {
        // Extract filename from Content-Disposition header
        const contentHeaders = response.headers.get('Content-Disposition');
        if (contentHeaders) {
          const filename = contentDisposition.parse(contentHeaders)?.parameters?.filename;
          // Browser assigns GUID if filename missing
          save.download = filename || '';
        } else if (downloadResource.value === 'rdf') {
          // RDF requires manual filename since server doesn't provide one
          save.download = 'rows.rdf';
        }
        return response.blob();
      })
      .then((blob) => this.processDownloadFile(blob, downloadResource, save))
      .catch((error) => {
        console.error('Error attempting export request', error);
        showToastNow({
          type: ToastType.ERROR,
          content: translate('export_error'),
          timeout: Infinity,
          hideId: toastHideId,
        });
      });
  };

  generateFileName = (viewName: string, format: ExportFormat): string => {
    const d = new Date();
    const dateString = [d.getFullYear(), d.getMonth() + 1, d.getDate()]
      .map((n, i) => (i > 0 ? String(n).padStart(2, '0') : String(n)))
      .join('');
    return `${viewName.replaceAll(' ', '_')}_${dateString}.${getExtensionForExportFormat(format)}`;
  };

  downloadFileRequestSoda3 = (downloadResource: ResourceType): void => {
    const save: HTMLAnchorElement = document.createElement('a');
    const headers = {
      ...defaultHeaders,
      Accept: getMimeType(downloadResource.value),
    };

    const options: RequestInit = {
      body: JSON.stringify(downloadResource.requestBody),
      credentials: 'same-origin',
      headers: headers,
      method: 'POST',
    };
    fetch(downloadResource.url, options)
      .then((r) => checkStatus(r, `Error exporting data in format ${downloadResource.value}`))
      .then((response) => {
        save.download = this.generateFileName(this.props.view.name, downloadResource.value);
        return response.blob();
      })
      .then((blob) => this.processDownloadFile(blob, downloadResource, save))
      .catch((error) => {
        console.error('Error attempting export request', error);
        showToastNow({
          type: ToastType.ERROR,
          content: translate('export_error'),
          timeout: Infinity,
          hideId: toastHideId,
        });
      });
  };

  processDownloadFile(blob: Blob, downloadResource: ResourceType, save: HTMLAnchorElement): void {
    const contentType = getMimeType(downloadResource.value);
    const url = windowURL.createObjectURL(new Blob([blob], { type: contentType }));
    save.target = '_blank';
    save.rel = 'noreferrer';
    save.setAttribute('style', 'display: none');
    document.body.appendChild(save);
    save.href = url;
    save.click();
    // Delay prevents race condition with URL revocation
    setTimeout(() => {
      windowURL.revokeObjectURL(url);
    }, 200);
    hideToastById(toastHideId);
    this.hideModal();
  }

  shouldShowFilteredDownload(): boolean {
    return !this.queryStringIsEmpty();
  }

  queryStringIsEmpty(): boolean {
    const { queryStringClause } = this.props;
    const result = isObject(queryStringClause)
      ? isEmpty(queryStringClause.whereClause)
      : isEmpty(queryStringClause) || queryStringClause!.toLowerCase() === 'select *';
    return result;
  }

  /** Check rendering permissions based on storyteller mode and feature flags. */
  checkRenderPermission(checkType: 'odata' | 'api' | 'export'): boolean {
    const isStoryteller = !isEmpty(window.STORY_DATA);
    const endpointSetting = FeatureFlags.valueOrDefault('enable_endpoints_in_stories');

    const result =
      checkType === 'odata'
        ? isStoryteller && endpointSetting !== 'none'
        : checkType === 'api'
          ? !isStoryteller || endpointSetting === 'odata_api'
          : !isStoryteller || endpointSetting !== 'none';
    return result;
  }

  shouldRenderOdataOption = () => this.checkRenderPermission('odata');
  shouldRenderApiOption = () => this.checkRenderPermission('api');
  shouldRenderExportOptions = () => this.checkRenderPermission('export');

  hideModal = () => {
    // Delay prevents memory leak from async Forge Overlay task
    const MODAL_DISMISS_DELAY = 300;
    this.setState({ isExportModalOpen: false }, () => {
      setTimeout(() => this.props.onDismiss(), MODAL_DISMISS_DELAY);
    });
  };

  render() {
    const { toggleOption } = this.state;
    const apiResourceUrl = this.state.resourceState.selectedApiResource.url;
    const odataResourceUrl = this.state.resourceState.selectedOdataResource.url;

    return (
      <ForgeDialog
        persistent={true}
        on-forge-dialog-close={this.hideModal}
        open={this.state.isExportModalOpen}
        aria-labelledby="export-dialog-title"
        aria-describedby="export-dialog-body"
      >
        <div className="export-dataset-dialog">
          <ForgeScaffold>
            <div slot="header">
              <ForgeToolbar>
                <h1
                  slot="start"
                  id="export-dialog-title"
                  className="forge-typography--subheading4 forge-header-title"
                >
                  {translate('export_dataset')}
                </h1>
                <ForgeIconButton
                  slot="end"
                  data-testid="export-modal-x"
                  className="tyler-icons"
                  onClick={this.hideModal}
                  aria-label={translate('close_dialog')}
                >
                  <ForgeIcon name="close" />
                </ForgeIconButton>
              </ForgeToolbar>
            </div>

            <div slot="body" className="export-dialog-container">
              <div>
                <p id="export-dialog-body" className="forge-typography--body1 body-text">
                  {this.props.bodyText}
                </p>
              </div>
              <div>
                {this.shouldRenderExportOptions() ? (
                  <ForgeButtonToggleGroup
                    id="export-button-group"
                    data-testid="export-toggle-buttons"
                    className="button-toggle"
                    mandatory={true}
                    value={toggleOption}
                    on-forge-button-toggle-group-change={(event: CustomEvent) =>
                      this.handleToggleChange(event.detail)
                    }
                  >
                    <ForgeButtonToggle
                      data-testid="export-toggle-download"
                      value={TOGGLE_OPTIONS.DOWNLOAD_FILE}
                      aria-label={translate('download_file')}
                      forge-dialog-focus="true"
                    >
                      {translate('download_file')}
                    </ForgeButtonToggle>
                    {this.shouldRenderApiOption() ? (
                      <ForgeButtonToggle
                        data-testid="export-toggle-api"
                        value={TOGGLE_OPTIONS.API_ENDPOINT}
                        aria-label={translate('api_endpoint')}
                      >
                        {translate('api_endpoint')}
                      </ForgeButtonToggle>
                    ) : null}
                    {this.shouldRenderOdataOption() ? (
                      <ForgeButtonToggle
                        data-testid="export-toggle-odata"
                        value={TOGGLE_OPTIONS.ODATA_ENDPOINT}
                        aria-label={translate('odata_endpoint')}
                      >
                        {translate('odata_endpoint')}
                      </ForgeButtonToggle>
                    ) : null}
                  </ForgeButtonToggleGroup>
                ) : null}
              </div>
              <div className="export-formats">
                {toggleOption === TOGGLE_OPTIONS.ODATA_ENDPOINT && this._OdataEndpoint()}
                {toggleOption === TOGGLE_OPTIONS.API_ENDPOINT && this.apiEndpoint()}
                {toggleOption === TOGGLE_OPTIONS.DOWNLOAD_FILE && this.downloadFile()}
              </div>
            </div>

            <div slot="footer" className="footer">
              <ForgeToolbar inverted={true}>
                <ForgeButton
                  slot="end"
                  variant="outlined"
                  data-testid="export-modal-cancel"
                  onClick={this.hideModal}
                >
                  {translate('cancel')}
                </ForgeButton>

                {this.state.toggleOption === TOGGLE_OPTIONS.DOWNLOAD_FILE && (
                  <ForgeButton
                    slot="end"
                    variant="raised"
                    data-testid="export-download-button"
                    onClick={this.onDownload}
                    disabled={!this.state.resourceState.selectedDownloadResource?.url}
                  >
                    {translate('download')}
                  </ForgeButton>
                )}
                {this.state.toggleOption === TOGGLE_OPTIONS.API_ENDPOINT && (
                  <CopyToClipboard
                    text={apiResourceUrl}
                    onCopy={() => {
                      this.onCopyApiToast();
                      this.hideModal();
                    }}
                  >
                    <ForgeButton
                      slot="end"
                      variant="raised"
                      data-testid="api-copy-button"
                      disabled={!apiResourceUrl}
                    >
                      {translate('copy_clipboard')}
                    </ForgeButton>
                  </CopyToClipboard>
                )}
                {this.state.toggleOption === TOGGLE_OPTIONS.ODATA_ENDPOINT && (
                  <CopyToClipboard
                    text={odataResourceUrl}
                    onCopy={() => {
                      this.onCopyOdataToast();
                      this.hideModal();
                    }}
                  >
                    <ForgeButton
                      slot="end"
                      variant="raised"
                      data-testid="odata-copy-button"
                      disabled={!odataResourceUrl}
                    >
                      {translate('copy_clipboard')}
                    </ForgeButton>
                  </CopyToClipboard>
                )}
              </ForgeToolbar>
            </div>
          </ForgeScaffold>
        </div>
      </ForgeDialog>
    );
  }
}
